import { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, Download, Package, Wrench, Users, Settings, Upload, FileDown, FileSpreadsheet, FileJson, FileCode, FileText, ChevronDown, Copy, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Button, Input, Textarea, Card, Badge, Modal } from "@/components/common";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { KiKorrekturWrapper } from "@/components/KiKorrekturWrapper";

const TYPE_CONFIG = {
  Artikel: { label: "Artikel", color: "bg-blue-100 text-blue-800", icon: Package },
  Leistung: { label: "Leistung", color: "bg-green-100 text-green-800", icon: Wrench },
  Fremdleistung: { label: "Fremdleistung", color: "bg-orange-100 text-orange-800", icon: Users },
};

const ArtikelModulPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTyp, setActiveTyp] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showDupes, setShowDupes] = useState(false);

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const res = await api.get("/modules/artikel/data");
      setItems(res.data);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); setTimeout(() => setConfirmDeleteId(null), 3000); return; }
    try {
      await api.delete(`/modules/artikel/data/${id}`);
      toast.success("Geloescht");
      setConfirmDeleteId(null);
      loadItems();
    } catch { toast.error("Fehler"); }
  };

  const handleExport = async (format) => {
    // format: "csv" | "excel" | "json" | "xml"
    const endpointMap = {
      csv: { path: "/modules/artikel/export-csv", ext: "csv", mime: "text/csv" },
      excel: { path: "/modules/artikel/export-excel", ext: "xlsx", mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      json: { path: "/modules/artikel/export-json", ext: "json", mime: "application/json" },
      xml: { path: "/modules/artikel/export-xml", ext: "xml", mime: "application/xml" },
    };
    const cfg = endpointMap[format];
    if (!cfg) return;
    try {
      const res = await api.get(cfg.path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: cfg.mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Artikel_Export_${new Date().toISOString().split("T")[0]}.${cfg.ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} Export erstellt`);
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  const handleImport = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const loadingToast = toast.loading(`Importiere ${file.name}...`);
    try {
      const res = await api.post("/modules/artikel/import-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.dismiss(loadingToast);
      toast.success(`${res.data.imported} importiert, ${res.data.skipped} uebersprungen (Duplikate)`);
      if (res.data.errors?.length > 0) toast.warning(`${res.data.errors.length} Zeilen mit Fehlern`);
      loadItems();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.response?.data?.detail || "Import fehlgeschlagen");
    }
    ev.target.value = "";
  };

  const handleVorlageDownload = async () => {
    try {
      const res = await api.get("/modules/artikel/import-vorlage", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Vorlage_Artikel_Import.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Vorlage konnte nicht geladen werden"); }
  };

  const filtered = items
    .filter(i => !activeTyp || i.typ === activeTyp)
    .filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.description || "").toLowerCase().includes(search.toLowerCase()) || (i.artikel_nr || "").toLowerCase().includes(search.toLowerCase()));

  const counts = { Artikel: 0, Leistung: 0, Fremdleistung: 0 };
  items.forEach(i => { if (counts[i.typ] !== undefined) counts[i.typ]++; });

  return (
    <div data-testid="artikel-modul-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4 lg:mb-8">
        <div>
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            <h1 className="text-2xl lg:text-4xl font-bold">Artikel & Leistungen</h1>
            <Badge variant="default" className="text-xs">Solo</Badge>
          </div>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">{items.length} Eintraege gesamt</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)} data-testid="btn-config-artikel">
            <Settings className="w-4 h-4" /> Konfiguration
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowDupes(true)} data-testid="btn-find-duplicates">
            <Copy className="w-4 h-4" /> Duplikate finden
          </Button>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-export-menu">
                <Download className="w-4 h-4" /> Export <ChevronDown className="w-3 h-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Format waehlen</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="export-csv">
                <FileText className="w-4 h-4 mr-2 text-blue-600" /> CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("excel")} data-testid="export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("json")} data-testid="export-json">
                <FileJson className="w-4 h-4 mr-2 text-amber-600" /> JSON (.json)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xml")} data-testid="export-xml">
                <FileCode className="w-4 h-4 mr-2 text-purple-600" /> XML (.xml)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Import Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-import-menu">
                <Upload className="w-4 h-4" /> Import <ChevronDown className="w-3 h-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Datei importieren</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); document.getElementById("artikel-import-file")?.click(); }} data-testid="import-file">
                <Upload className="w-4 h-4 mr-2" /> Datei auswaehlen...
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Unterstuetzt</DropdownMenuLabel>
              <div className="px-2 pb-2 flex flex-wrap gap-1 text-xs">
                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">.csv</span>
                <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700">.xlsx</span>
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">.json</span>
                <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">.xml</span>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleVorlageDownload} data-testid="btn-vorlage-download">
                <FileDown className="w-4 h-4 mr-2" /> CSV-Vorlage herunterladen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input id="artikel-import-file" type="file" className="hidden" accept=".csv,.CSV,.xlsx,.xls,.json,.xml" onChange={handleImport} data-testid="input-import-file" />

          <Button size="sm" className="lg:h-10 lg:px-4" onClick={() => { setEditItem(null); setShowModal(true); }} data-testid="btn-new-artikel">
            <Plus className="w-4 h-4" /> Neu
          </Button>
        </div>
      </div>

      {/* Konfiguration */}
      {showConfig && <ArtikelConfig onClose={() => setShowConfig(false)} />}

      {/* Typ Filter */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="artikel-typ-filter">
        <button onClick={() => setActiveTyp("")}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${!activeTyp ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
          Alle ({items.length})
        </button>
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button key={key} onClick={() => setActiveTyp(activeTyp === key ? "" : key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${activeTyp === key ? cfg.color + " shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              <Icon className="w-3.5 h-3.5" /> {cfg.label} ({counts[key] || 0})
            </button>
          );
        })}
      </div>

      <Card className="p-3 lg:p-4 mb-4 lg:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-9 h-9 lg:h-10" placeholder="Suchen..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="input-search-artikel" />
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Laden...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{search || activeTyp ? "Keine Ergebnisse" : "Erstellen Sie Artikel, Leistungen oder Fremdleistungen"}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const cfg = TYPE_CONFIG[item.typ] || TYPE_CONFIG.Artikel;
            const Icon = cfg.icon;
            return (
              <Card key={item.id} className="overflow-hidden" data-testid={`artikel-${item.id}`}>
                <div className="flex items-center gap-4 p-3 lg:p-4">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {item.artikel_nr && <span className="text-xs font-mono text-muted-foreground">{item.artikel_nr}</span>}
                      <span className="font-semibold truncate">{item.name}</span>
                      <Badge className={`text-xs ${cfg.color}`}>{cfg.label}</Badge>
                    </div>
                    {item.description && <p className="text-sm text-muted-foreground truncate mt-0.5">{item.description}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-muted-foreground text-sm">{item.unit}</span>
                    <div className="text-right">
                      <span className="font-mono font-semibold">{(item.price_net || 0).toFixed(2)} EUR</span>
                      {item.ek_preis > 0 && <p className="text-xs text-muted-foreground">EK: {item.ek_preis.toFixed(2)} EUR</p>}
                      {item.labor_cost > 0 && <p className="text-xs text-blue-600">Lohn: {item.labor_cost.toFixed(2)} EUR</p>}
                    </div>
                    <button onClick={() => { setEditItem(item); setShowModal(true); }} className="p-2 hover:bg-muted rounded-sm" title="Bearbeiten">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item.id)}
                      className={`p-2 rounded-sm transition-colors ${confirmDeleteId === item.id ? "bg-red-500 text-white" : "hover:bg-destructive/10"}`}>
                      {confirmDeleteId === item.id ? <span className="text-xs font-bold">?</span> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ArtikelFormModal isOpen={showModal} onClose={() => setShowModal(false)} item={editItem} onSave={() => { setShowModal(false); loadItems(); }} />
      {showDupes && <DuplicateFinderModal onClose={() => setShowDupes(false)} onCleaned={() => { setShowDupes(false); loadItems(); }} />}
    </div>
  );
};


// ==================== FORM MODAL ====================
const ArtikelFormModal = ({ isOpen, onClose, item, onSave }) => {
  const [form, setForm] = useState({ name: "", description: "", typ: "Artikel", unit: "Stk.", ek_preis: 0, price_net: 0, subunternehmer: "", artikel_nr: "", labor_cost: 0 });
  const [loading, setLoading] = useState(false);
  const [kalkEk, setKalkEk] = useState(0);
  const [kalkAufschlag, setKalkAufschlag] = useState(0);

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name || "", description: item.description || "", typ: item.typ || "Artikel",
        unit: item.unit || "Stk.", ek_preis: item.ek_preis || 0, price_net: item.price_net || 0,
        subunternehmer: item.subunternehmer || "", artikel_nr: item.artikel_nr || "",
        labor_cost: item.labor_cost || 0,
      });
      setKalkEk(item.ek_preis || 0);
      setKalkAufschlag(item.ek_preis > 0 ? Math.round((item.price_net / item.ek_preis - 1) * 100) : 0);
    } else {
      setForm({ name: "", description: "", typ: "Artikel", unit: "Stk.", ek_preis: 0, price_net: 0, subunternehmer: "", artikel_nr: "", labor_cost: 0 });
      setKalkEk(0);
      setKalkAufschlag(0);
      // Auto-Nummer laden
      api.get("/modules/artikel/next-number/Artikel").then(res => setForm(f => ({ ...f, artikel_nr: res.data.nummer }))).catch(() => {});
    }
  }, [item]);

  const handleTypChange = (newTyp) => {
    setForm(f => ({ ...f, typ: newTyp, unit: newTyp !== "Artikel" ? "Stunde" : f.unit }));
    // Neue Nummer fuer diesen Typ laden
    if (!item) {
      api.get(`/modules/artikel/next-number/${newTyp}`).then(res => setForm(f => ({ ...f, artikel_nr: res.data.nummer }))).catch(() => {});
    }
  };

  const recalc = (ek, aufschlag) => {
    const vk = ek * (1 + aufschlag / 100);
    setForm(f => ({ ...f, price_net: Math.round(vk * 100) / 100, ek_preis: ek }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Bezeichnung erforderlich"); return; }
    setLoading(true);
    try {
      if (item) {
        await api.put(`/modules/artikel/data/${item.id}`, form);
        toast.success("Aktualisiert");
      } else {
        await api.post("/modules/artikel/data", form);
        toast.success("Erstellt");
      }
      onSave();
    } catch { toast.error("Fehler"); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={item ? "Bearbeiten" : "Neu erstellen"} size="xl">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="artikel-form-modal">
        {/* Typ */}
        <div>
          <label className="block text-sm font-medium mb-2">Typ</label>
          <div className="flex gap-2">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button key={key} type="button"
                  onClick={() => handleTypChange(key)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all flex items-center gap-1 ${form.typ === key ? cfg.color + " shadow-sm ring-2 ring-offset-1 ring-primary/30" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                  <Icon className="w-3.5 h-3.5" /> {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Artikel-Nr.</label>
            <Input value={form.artikel_nr} onChange={(e) => setForm({ ...form, artikel_nr: e.target.value })} placeholder="z.B. ART-001" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Einheit</label>
            <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm">
              {["Stk.", "Stunde", "m", "m2", "m3", "kg", "Psch.", "km", "Liter", "Set"].map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Bezeichnung *</label>
          <KiKorrekturWrapper
            value={form.name || ""}
            onChange={(v) => setForm({ ...form, name: v })}
            kontext="allgemein"
            feldLabel="Bezeichnung"
            testId="btn-ki-artikel-name"
            iconPosition="top-right"
          >
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="input-artikel-name" className="pr-9" />
          </KiKorrekturWrapper>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Beschreibung</label>
          <KiKorrekturWrapper
            value={form.description || ""}
            onChange={(v) => setForm({ ...form, description: v })}
            kontext="allgemein"
            feldLabel="Artikel-Beschreibung"
            testId="btn-ki-artikel-desc"
          >
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="pr-9" />
          </KiKorrekturWrapper>
        </div>

        {/* Kalkulation */}
        <div className="border rounded-sm p-3 bg-muted/20">
          <label className="block text-sm font-semibold mb-2">Preiskalkulation</label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">EK-Preis (Netto)</label>
              <Input type="number" step="0.01" value={kalkEk}
                onChange={(e) => { const v = parseFloat(e.target.value) || 0; setKalkEk(v); recalc(v, kalkAufschlag); }} />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Aufschlag %</label>
              <Input type="number" value={kalkAufschlag}
                onChange={(e) => { const v = parseFloat(e.target.value) || 0; setKalkAufschlag(v); recalc(kalkEk, v); }} />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">VK-Preis (Netto)</label>
              <Input type="number" step="0.01" value={form.price_net}
                onChange={(e) => setForm({ ...form, price_net: parseFloat(e.target.value) || 0 })} data-testid="input-artikel-price" />
            </div>
          </div>
        </div>

        {/* Subunternehmer (nur bei Fremdleistung) */}
        {form.typ === "Fremdleistung" && (
          <div>
            <label className="block text-sm font-medium mb-2">Subunternehmer</label>
            <Input value={form.subunternehmer} onChange={(e) => setForm({ ...form, subunternehmer: e.target.value })} placeholder="Firmenname" />
          </div>
        )}

        {/* Lohnanteil (nur bei Leistung/Fremdleistung) */}
        {(form.typ === "Leistung" || form.typ === "Fremdleistung") && (
          <div>
            <label className="block text-sm font-medium mb-2">Lohnanteil (netto pro Einheit)</label>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" value={form.labor_cost || ""} onChange={(e) => setForm({ ...form, labor_cost: parseFloat(e.target.value) || 0 })} placeholder="0.00" className="font-mono" data-testid="input-labor-cost" />
              <span className="text-sm text-muted-foreground">€</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Wird automatisch in Angebot/Rechnung uebernommen</p>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" disabled={loading} data-testid="btn-save-artikel">
            {loading ? "Speichern..." : "Speichern"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};


// ==================== KONFIGURATION ====================
const ArtikelConfig = ({ onClose }) => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/modules/artikel/config").then(res => setConfig(res.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/modules/artikel/config", config);
      setConfig(res.data);
      toast.success("Konfiguration gespeichert");
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  if (!config) return null;

  return (
    <Card className="p-4 mb-4 border-primary/20 bg-primary/5" data-testid="artikel-config">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold flex items-center gap-2"><Settings className="w-4 h-4" /> Nummernvergabe konfigurieren</h3>
        <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Schliessen</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Artikel */}
        <div className="space-y-2 p-3 border rounded-sm bg-background">
          <h4 className="text-sm font-semibold text-blue-700 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Artikel</h4>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Prefix</label>
            <Input value={config.artikel_prefix || ""} onChange={(e) => setConfig({ ...config, artikel_prefix: e.target.value })} placeholder="ArtNr" data-testid="config-artikel-prefix" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Startnummer</label>
            <Input type="number" value={config.artikel_start || 0} onChange={(e) => setConfig({ ...config, artikel_start: parseInt(e.target.value) || 0 })} data-testid="config-artikel-start" />
          </div>
          <p className="text-xs text-muted-foreground">Beispiel: <span className="font-mono font-bold">{config.artikel_prefix}{config.artikel_start}</span></p>
        </div>
        {/* Leistung */}
        <div className="space-y-2 p-3 border rounded-sm bg-background">
          <h4 className="text-sm font-semibold text-green-700 flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Leistung</h4>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Prefix</label>
            <Input value={config.leistung_prefix || ""} onChange={(e) => setConfig({ ...config, leistung_prefix: e.target.value })} placeholder="Leist" data-testid="config-leistung-prefix" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Startnummer</label>
            <Input type="number" value={config.leistung_start || 0} onChange={(e) => setConfig({ ...config, leistung_start: parseInt(e.target.value) || 0 })} data-testid="config-leistung-start" />
          </div>
          <p className="text-xs text-muted-foreground">Beispiel: <span className="font-mono font-bold">{config.leistung_prefix}{config.leistung_start}</span></p>
        </div>
        {/* Fremdleistung */}
        <div className="space-y-2 p-3 border rounded-sm bg-background">
          <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Fremdleistung</h4>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Prefix</label>
            <Input value={config.fremdleistung_prefix || ""} onChange={(e) => setConfig({ ...config, fremdleistung_prefix: e.target.value })} placeholder="Fremd" data-testid="config-fremd-prefix" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Startnummer</label>
            <Input type="number" value={config.fremdleistung_start || 0} onChange={(e) => setConfig({ ...config, fremdleistung_start: parseInt(e.target.value) || 0 })} data-testid="config-fremd-start" />
          </div>
          <p className="text-xs text-muted-foreground">Beispiel: <span className="font-mono font-bold">{config.fremdleistung_prefix}{config.fremdleistung_start}</span></p>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={handleSave} disabled={saving} size="sm" data-testid="btn-save-config">
          {saving ? "Speichern..." : "Speichern"}
        </Button>
      </div>
    </Card>
  );
};

export { ArtikelModulPage };


// ==================== DUPLICATE FINDER MODAL ====================
const DuplicateFinderModal = ({ onClose, onCleaned }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({}); // { groupIdx: Set(ids to delete) }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/modules/artikel/find-duplicates").then(res => {
      setData(res.data);
      // Preselect: alle delete_suggestions per default angehakt
      const init = {};
      (res.data.groups || []).forEach((g, idx) => {
        init[idx] = new Set(g.delete_suggestions.map(d => d.id));
      });
      setSelected(init);
    }).catch(() => toast.error("Fehler beim Suchen"))
      .finally(() => setLoading(false));
  }, []);

  const toggleId = (groupIdx, id) => {
    setSelected(prev => {
      const s = new Set(prev[groupIdx] || []);
      if (s.has(id)) s.delete(id); else s.add(id);
      return { ...prev, [groupIdx]: s };
    });
  };

  const totalToDelete = Object.values(selected).reduce((sum, s) => sum + (s?.size || 0), 0);

  const handleDelete = async () => {
    const allIds = Object.values(selected).flatMap(s => [...(s || [])]);
    if (allIds.length === 0) { toast.error("Nichts ausgewaehlt"); return; }
    if (!window.confirm(`${allIds.length} Eintraege wirklich unwiderruflich loeschen?`)) return;
    setDeleting(true);
    try {
      const res = await api.post("/modules/artikel/bulk-delete", { ids: allIds });
      toast.success(`${res.data.deleted} Duplikate geloescht`);
      onCleaned();
    } catch { toast.error("Fehler beim Loeschen"); }
    finally { setDeleting(false); }
  };

  const typColors = { Artikel: "bg-blue-100 text-blue-800", Leistung: "bg-green-100 text-green-800", Fremdleistung: "bg-orange-100 text-orange-800" };

  return (
    <Modal isOpen={true} onClose={onClose} title="Duplikate finden & aufraeumen" size="xl">
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Suche Duplikate...</div>
      ) : !data || data.duplicate_groups === 0 ? (
        <div className="text-center py-12" data-testid="no-duplicates">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
          <p className="font-semibold">Keine Duplikate gefunden</p>
          <p className="text-sm text-muted-foreground mt-1">Alle {data?.total_items || 0} Eintraege sind einzigartig. Top!</p>
          <Button onClick={onClose} className="mt-4">Schliessen</Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="duplicates-modal">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p><strong>{data.duplicate_groups} Gruppen</strong> mit insgesamt <strong>{data.total_duplicates_found} Duplikaten</strong> gefunden.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Gruene Zeilen = empfohlen zu behalten (meiste Details). Rote Zeilen = Loeschvorschlag.
                Haken an = wird beim Klick auf &quot;Loeschen&quot; entfernt. Du kannst frei umentscheiden.
              </p>
            </div>
          </div>

          <div className="max-h-[55vh] overflow-y-auto space-y-3 pr-2">
            {data.groups.map((g, idx) => (
              <div key={idx} className="border rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={typColors[g.typ] || "bg-gray-100"}>{g.typ}</Badge>
                  <span className="font-semibold text-sm">{g.keep_recommended.name}</span>
                  <span className="text-xs text-muted-foreground">({g.count} gefunden)</span>
                </div>
                {/* Keep-recommended Zeile */}
                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-sm mb-1 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-muted-foreground">{g.keep_recommended.artikel_nr || "—"}</span>
                    <span className="truncate">{g.keep_recommended.name}</span>
                    <span className="text-xs text-muted-foreground">{g.keep_recommended.unit}</span>
                    <span className="font-mono text-xs">{(g.keep_recommended.price_net || 0).toFixed(2)} EUR</span>
                    {g.keep_recommended.description && <span className="text-xs text-muted-foreground italic truncate max-w-[200px]">"{g.keep_recommended.description.slice(0, 50)}"</span>}
                    <Badge className="bg-green-100 text-green-700 text-xs">Score: {g.keep_recommended.score}</Badge>
                  </div>
                  <span className="text-xs font-medium text-green-700">Behalten</span>
                </div>
                {/* Delete candidates */}
                {g.delete_suggestions.map(d => (
                  <label key={d.id} className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-sm mb-1 text-sm cursor-pointer hover:bg-red-100" data-testid={`dupe-item-${d.id}`}>
                    <input
                      type="checkbox"
                      checked={selected[idx]?.has(d.id) || false}
                      onChange={() => toggleId(idx, d.id)}
                      className="flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs text-muted-foreground">{d.artikel_nr || "—"}</span>
                      <span className="truncate">{d.name}</span>
                      <span className="text-xs text-muted-foreground">{d.unit}</span>
                      <span className="font-mono text-xs">{(d.price_net || 0).toFixed(2)} EUR</span>
                      {d.description && <span className="text-xs text-muted-foreground italic truncate max-w-[200px]">"{d.description.slice(0, 50)}"</span>}
                      <Badge className="bg-gray-100 text-gray-700 text-xs">Score: {d.score}</Badge>
                    </div>
                    <span className="text-xs font-medium text-red-700">Loeschen</span>
                  </label>
                ))}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-3 border-t">
            <div className="text-sm">
              <strong className="text-red-700">{totalToDelete}</strong> Eintraege zum Loeschen markiert
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Abbrechen</Button>
              <Button onClick={handleDelete} disabled={deleting || totalToDelete === 0} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-delete-duplicates">
                <Trash2 className="w-4 h-4 mr-1" /> {deleting ? "Loesche..." : `${totalToDelete} Duplikate loeschen`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
